import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { faTag } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components/Icon/Icon";
import { classNames } from "@/utils/classNames";

import { buildVariableToken, VariableRow } from "../utils/variableRows";

interface VariableSectionRowProps {
	row: VariableRow;
	onInsert: (row: VariableRow) => void;
	/** Icon shown in the row badge — defaults to the generic tag. */
	icon?: IconDefinition;
	/** Tailwind classes for the icon badge (bg + text color). */
	iconClassName?: string;
}

const DEFAULT_ICON_CLASS = "bg-background-light text-text-meta";

/**
 * One row of the variables section: tag icon, variable name, the value
 * resolved for the current lead and an "In message" / "Insert" status badge.
 * Clicking the row inserts the variable at the editor cursor; rows can also
 * be dragged into the editor.
 */
export const VariableSectionRow: FunctionComponent<VariableSectionRowProps> = ({
	row,
	onInsert,
	icon = faTag,
	iconClassName = DEFAULT_ICON_CLASS,
}) => {
	const { t } = useTranslation();

	return (
		<div
			className="group w-full flex items-center gap-2 px-2.5 py-2 rounded-md border border-transparent text-left transition-colors cursor-pointer hover:bg-background-light hover:border-border-light"
			data-variable-key={row.key}
			draggable
			role="button"
			onClick={() => onInsert(row)}
			onDragStart={(e: React.DragEvent<HTMLDivElement>) =>
				e.dataTransfer?.setData("text", buildVariableToken(row.key, row.label))
			}
		>
			<span
				className={classNames(
					"w-[22px] h-[22px] flex-shrink-0 rounded flex items-center justify-center",
					iconClassName
				)}
			>
				<Icon className="w-3 h-3" icon={icon} />
			</span>

			<span className="flex-shrink-0 max-w-[55%] overflow-hidden text-ellipsis whitespace-nowrap text-[12.5px] font-medium text-text">
				{row.label}
			</span>

			{row.sampleValue && (
				<span className="flex-shrink min-w-0 overflow-hidden text-ellipsis whitespace-nowrap text-xs text-text-meta">
					{row.sampleValue}
				</span>
			)}

			<span className="flex-1 min-w-2" />

			<span
				className={classNames(
					"flex-shrink-0 text-xxs font-medium px-1.5 py-0.5 rounded",
					row.isInUse
						? "bg-status-success-light text-status-success"
						: "bg-background-light text-text-meta"
				)}
			>
				{row.isInUse ? t("variables.section.inMessage") : t("variables.section.insert")}
			</span>
		</div>
	);
};
